import { cloneDeep } from "lodash";

import {
  selectDatesRange,
  selectPeriod,
  selectDevice,
  selectGroup,
  selectSelectedAppType,
  selectApps
} from "../state/selectors";
import { getState } from "../state/store";

import {
  advancedDiagnosticCodes,
  chartMetricsConfig,
  dateRangeConfig,
  dateRangeFilterOptions,
  deviceKitNameType,
  deviceTypeColors,
  eventChartsGroups,
  eventStatuses,
  labelsAppearance,
  rumEventsByCharts,
  rumEventsGroups,
  settingNames
} from "../consts";
import { dateUtil, deviceUtil, objectUtil } from "../utils";

const getSelectedGroupsAvailableEvents = (eventsList, selectedApps) => {
  return selectedApps
    ? eventsList.filter((event) => {
        return selectedApps.some((app) => {
          return rumEventsByCharts[event].some((e) => {
            return app.availableDashboards.some((d) => d.name === e && d.isSelected);
          });
        });
      })
    : eventsList;
};

const getSelectedGroupsAvailableCharts = (charts, selectedApps) => {
  return selectedApps
    ? charts.filter((chart) =>
        selectedApps.some((app) =>
          app.availableDashboards.some((d) => d.name === chart && d.isSelected)
        )
      )
    : charts;
};

const groupData = (events, formatter) => {
  const grouped = objectUtil.groupBy(events, formatter);

  return grouped;
};

const groupDataByTimestamps = (events, formatFn) => {
  const grouped = groupData(events, (event) => dateUtil[formatFn](new Date(event?.eventTimestamp)));

  return grouped;
};

const filterEvents = (events, devices) => {
  if (devices && devices?.length > 0) {
    if (devices[0] === deviceKitNameType.DEV_TYPE_KIT) {
      return events;
    }

    const filtered = events?.filter((event) => {
      return devices.includes(
        event?.eventDetails?.deviceTypeName || event?.eventDetails?.deviceName
      );
    });

    return filtered;
  }

  return events;
};

const filterEventsBySetting = (events, setting) => {
  const filtered = events?.filter((event) => event?.eventDetails?.settingName === setting);

  return filtered;
};

const getConfigForPeriod = (period) => {
  return dateRangeConfig.find((el) => el.id === period)?.config;
};

const getDateRangeConfig = (period, range, isReportConfig) => {
  let config = getConfigForPeriod(period);

  // For csv reports dates for the daily options should be displayed in dd/mm/yyyy format
  if (
    isReportConfig &&
    config &&
    [dateRangeFilterOptions.THIS_WEEK, dateRangeFilterOptions.LAST_7_DAYS].includes(period)
  ) {
    config = getConfigForPeriod(dateRangeFilterOptions.LAST_30_DAYS);
  }

  if (!config) {
    const calculatedRange = dateUtil.calculateRange(period);
    let from = calculatedRange?.fromDate;
    let to = calculatedRange?.toDate;

    if (!from || !to) {
      from = range && range?.from;
      to = range && range?.to;
    }

    if (from && to) {
      const daysDiff = dateUtil.getDaysDiff(from, to);

      if (daysDiff > 60) {
        config = getConfigForPeriod(dateRangeFilterOptions.LAST_12_MONTHS);
      } else if (daysDiff > 10) {
        config = getConfigForPeriod(dateRangeFilterOptions.LAST_30_DAYS);
      } else if (daysDiff > 2) {
        config = isReportConfig
          ? getConfigForPeriod(dateRangeFilterOptions.LAST_30_DAYS)
          : getConfigForPeriod(dateRangeFilterOptions.LAST_7_DAYS);
      } else {
        config = getConfigForPeriod(dateRangeFilterOptions.LAST_24_HOURS);
      }
    } else {
      config = {
        formatFn: null,
        dateListGetter: null,
        appearance: labelsAppearance.HORIZONTAL
      };
    }
  }

  return config;
};

const prepareChartData = ({ data, groupFn, devices, isReportConfig, setting }) => {
  const period = selectPeriod()(getState());
  const range = selectDatesRange()(getState());

  let filteredEvents = filterEvents(data, devices);
  if (setting) {
    filteredEvents = filterEventsBySetting(filteredEvents, setting);
  }

  const { formatFn, dateListGetter } = getDateRangeConfig(period, range, isReportConfig);

  if (!formatFn || !dateListGetter) {
    return null;
  }

  const grouped = groupFn
    ? groupData(filteredEvents, groupFn)
    : groupDataByTimestamps(filteredEvents, formatFn);

  const dates = dateUtil.getFormattedDatesList(period, dateListGetter, formatFn, range);

  return {
    grouped,
    dates
  };
};

const getDeviceTypeColor = (device) => {
  let color;

  const isDeviceType = deviceKitNameType[device];

  if (isDeviceType) {
    color = deviceTypeColors[device];
  } else {
    const deviceTypes = Object.keys(deviceKitNameType).filter((key) => {
      return deviceKitNameType[key] === device;
    });

    if (deviceTypes) {
      color = deviceTypeColors[deviceTypes[0]];
    }
  }

  return color;
};

const getFilteredMetricsConfig = (config, selectedSetting) => {
  if (!selectedSetting) {
    return config;
  }
  const filteredConfig = {};

  if (selectedSetting && config[selectedSetting]) {
    filteredConfig[selectedSetting] = config[selectedSetting];
  }

  filteredConfig.speed = config.speed;

  return filteredConfig;
};

const getMetricsConfig = ({
  chartName,
  deviceType,
  coloredMetric,
  coloredMetrics,
  updateMetricNameOnFilter,
  setting
}) => {
  const config = chartMetricsConfig.find((config) => config.id === chartName);

  if (!config?.metrics) {
    return;
  }
  let metricsConfig = cloneDeep(config.metrics);

  if (setting) {
    metricsConfig = getFilteredMetricsConfig(metricsConfig, settingNames[setting]);
  }

  if (deviceType) {
    if (coloredMetric) {
      const color = getDeviceTypeColor(deviceType[0]);

      if (color) {
        metricsConfig[coloredMetric].color = color;
      }

      if (updateMetricNameOnFilter) {
        metricsConfig[coloredMetric].name = deviceType[0];
      }
    }

    if (coloredMetrics) {
      const deviceColor = getDeviceTypeColor(deviceType[0]);
      const holderColor = deviceType[1] && getDeviceTypeColor(deviceType[1]);

      if (deviceColor) {
        if (!holderColor) {
          metricsConfig[coloredMetrics?.mono].color = deviceColor;

          if (metricsConfig[coloredMetrics?.duo]) {
            metricsConfig[coloredMetrics?.duo].color = deviceColor;
          }

          if (updateMetricNameOnFilter) {
            metricsConfig[coloredMetrics?.mono].name = deviceType[0];

            if (metricsConfig[coloredMetrics?.duo]) {
              metricsConfig[coloredMetrics?.duo].name = deviceType[0];
            }
          }
        } else {
          metricsConfig[coloredMetrics?.charger].color = deviceColor;
          metricsConfig[coloredMetrics?.holder].color = holderColor;

          if (updateMetricNameOnFilter) {
            metricsConfig[coloredMetrics?.charger].name = deviceType[0];
            metricsConfig[coloredMetrics?.holder].name = deviceType[1];
          }
        }
      }
    }
  }

  return metricsConfig;
};

const formatSettingName = (setting) => {
  return setting && settingNames[setting];
};

const formatDiagnosticsResult = (eventDetails) => {
  let result;

  if (eventDetails?.status === eventStatuses.FAILED) {
    result = "Failed";
    return result;
  }

  if (eventDetails?.result?.L0) {
    if (Object.keys(advancedDiagnosticCodes).includes(eventDetails.result.L0)) {
      result = eventDetails.result.L0;
    } else {
      result = "Undefined";
    }
  } else {
    result = "Undefined";
  }

  return result;
};

const filterResults = ({ result, agregatorName = "date", hasGroups, metricsConfig }) => {
  const device = selectDevice()(getState());

  const hasDeviceSelected = device && device?.id !== 0;

  const filteredResult = hasDeviceSelected
    ? result.map((res) => {
        const filteredMetrics = res.metrics;

        if (!hasGroups?.monoDevices && metricsConfig?.mono?.id) {
          delete filteredMetrics[metricsConfig.mono.id];
        }

        if (!hasGroups?.chargers && metricsConfig?.charger?.id) {
          delete filteredMetrics[metricsConfig.charger.id];
        }

        if (!hasGroups?.holders && metricsConfig?.holder?.id) {
          delete filteredMetrics[metricsConfig.holder.id];
        }

        if (!hasGroups?.duoDevices && metricsConfig?.duo?.id) {
          delete filteredMetrics[metricsConfig.duo.id];
        }

        return {
          [agregatorName]: res[agregatorName],
          metrics: filteredMetrics
        };
      })
    : result;

  return filteredResult;
};

const checkForSuccessButNotRecconectedFWU = (event) => {
  const result = { isSuccessButNotReconnected: false, isHolder: false };

  if (
    event?.eventDetails?.duration !== undefined &&
    event?.eventDetails?.result?.status === eventStatuses.SUCCESS
  ) {
    return result;
  } else {
    const eventError = event?.eventDetails?.result?.error;

    if (eventError) {
      let errorMessage;

      try {
        errorMessage = eventError?.errorMessage && JSON.parse(eventError.errorMessage);
      } catch (e) {
        errorMessage = null;
      }

      const successButNotReconnectedErrorCodes = ["dev-lyr-coma-6"];
      const isSuccessButNotReconnected =
        errorMessage && successButNotReconnectedErrorCodes.includes(errorMessage?.code);

      if (isSuccessButNotReconnected) {
        result.isSuccessButNotReconnected = true;

        // Exclude data for holder from the statistics
        const deviceTypeName = event?.eventDetails?.deviceTypeName;

        const deviceGroup = deviceUtil.getDeviceGroup(deviceTypeName);
        const isHolder = deviceGroup === "holders";

        if (isHolder) {
          result.isHolder = true;
        }
      }
    }

    return result;
  }
};

const getFWUStatistics = (events) => {
  let successfulFWU = 0;
  let successfulNotReconnectedFWU = 0;
  let failedFWU = 0;
  let total = 0;

  if (events && events?.length > 0) {
    events.forEach((event) => {
      let excludeEventFromStats = false;

      if (
        event?.eventDetails?.duration !== undefined &&
        event?.eventDetails?.result?.status === eventStatuses.SUCCESS
      ) {
        successfulFWU++;
      } else {
        // exclude errors with outerErrorCode 32 from stats
        if (event?.eventDetails?.result?.error?.outerErrorCode == 32) {
          excludeEventFromStats = true;
        } else {
          // check for success but not reconnected error
          const { isSuccessButNotReconnected, isHolder } =
            checkForSuccessButNotRecconectedFWU(event);

          if (isSuccessButNotReconnected) {
            if (!isHolder) {
              successfulNotReconnectedFWU++;
            } else {
              excludeEventFromStats = true;
            }
          } else {
            failedFWU++;
          }
        }
      }

      if (!excludeEventFromStats) {
        total++;
      }
    });
  }

  return {
    successfulFWU,
    successfulNotReconnectedFWU,
    failedFWU,
    total
  };
};

const getAdvancedDiagnosticsStatistics = (events) => {
  let workingProperlyAD = 0;
  let defectiveAD = 0;
  let failedAD = 0;
  let unknownAD = 0;
  let undefinedAD = 0;

  if (events && events?.length > 0) {
    events.forEach((event) => {
      const advancedDiagnosticResult = event?.eventDetails?.result;
      const L0 = advancedDiagnosticResult?.L0;
      const deviceState = advancedDiagnosticResult?.device_state?.toLowerCase();

      if (event?.eventDetails?.status === eventStatuses.FAILED) {
        failedAD += 1;
        return;
      }

      if (L0 === "B999" || !Object.keys(advancedDiagnosticCodes).includes(L0)) {
        undefinedAD += 1;
        return;
      }

      if (deviceState) {
        if (["working_properly", "good"].includes(deviceState)) {
          workingProperlyAD += 1;
        }

        if (["defective", "defected"].includes(deviceState)) {
          defectiveAD += 1;
        }

        if (["unknown", "unrecognized"].includes(deviceState)) {
          unknownAD += 1;
        }

        if (["undefined", "no_device"].includes(deviceState)) {
          undefinedAD += 1;
        }
      } else {
        undefinedAD += 1;
      }
    });
  }

  return {
    workingProperlyAD,
    defectiveAD,
    failedAD,
    unknownAD,
    undefinedAD
  };
};

const getEventsBySelectedGroups = (events) => {
  const selectedGroups = selectGroup()(getState());
  const groups = selectedGroups?.map((g) => g.id);
  const appType = selectSelectedAppType()(getState());
  const apps = selectApps()(getState());

  const selectedApps = apps?.filter((app) => appType.some((type) => type.id === app.appType));

  if (groups.includes(0)) {
    const eventsList = events ? events : Object.values(rumEventsGroups).flat();

    return getSelectedGroupsAvailableEvents(eventsList, selectedApps);
  }

  const result = [];
  groups.forEach((group) => result.push(...rumEventsGroups[group]));

  const eventsList = events ? result.filter((e) => events.includes(e)) : result;

  return getSelectedGroupsAvailableEvents(eventsList, selectedApps);
};

const getChartsBySelectedGroups = (charts) => {
  const selectedGroups = selectGroup()(getState());
  const groups = selectedGroups?.map((g) => g.id);
  const appType = selectSelectedAppType()(getState());
  const apps = selectApps()(getState());

  const selectedApps = apps?.filter((app) => appType.some((type) => type.id === app.appType));

  if (groups.includes(0)) {
    return getSelectedGroupsAvailableCharts(charts, selectedApps);
  }

  const result = [];
  groups.forEach((group) => {
    result.push(...eventChartsGroups[group]);
  });

  const filteredCharts = result.filter((chart) => charts.includes(chart));

  return getSelectedGroupsAvailableCharts(filteredCharts, selectedApps);
};

const rumEventsUtil = {
  prepareChartData,
  getDateRangeConfig,
  getMetricsConfig,
  formatSettingName,
  formatDiagnosticsResult,
  filterResults,
  getFWUStatistics,
  getAdvancedDiagnosticsStatistics,
  checkForSuccessButNotRecconectedFWU,
  filterEventsBySetting,
  getEventsBySelectedGroups,
  getChartsBySelectedGroups
};

export default rumEventsUtil;
